
import React, { useState, useEffect, useRef } from 'react';
import { UserProfile, VacationRange } from '../../types';
import { supabase } from '../../services/supabaseClient';
import { companyFilter } from '../../services/companyScope';
import { Button } from '../Button';
import { X, User, Lock, Palmtree, Save, Loader2, Plus, Trash2, Calendar, AlertCircle, Phone, MapPin, Palette, Home, Briefcase, Layers, Check } from 'lucide-react';
import { DesignMode, designFromSettings, setDesignMode, useDesignMode } from '../../design/glassMode';
import { patchProfileSettings } from '../../services/cloudService';

interface Props {
    user: UserProfile;
    onClose: () => void;
    onUpdateProfile: (updates: Partial<UserProfile>) => Promise<void>;
    /** Einstellungen (Kartenfarbe, Optik) gespeichert — Profil neu laden. */
    onEinstellungenGespeichert?: () => void;
    /** Ohne Angabe (Handy-App) entfaellt „Vertreter planen“. */
    onOpenHolidayPlanner?: (startDate?: string, endDate?: string) => void;
}

// 50 Standard Colors for Map Markers
const MAP_COLORS = [
    '#ef4444', '#f87171', '#b91c1c', '#991b1b', // Red
    '#f97316', '#fb923c', '#c2410c', '#9a3412', // Orange
    '#f59e0b', '#fbbf24', '#b45309', '#78350f', // Amber
    '#eab308', '#fcd34d', '#a16207', '#713f12', // Yellow
    '#84cc16', '#a3e635', '#4d7c0f', '#365314', // Lime
    '#22c55e', '#4ade80', '#15803d', '#14532d', // Green
    '#10b981', '#34d399', '#047857', '#064e3b', // Emerald
    '#14b8a6', '#2dd4bf', '#0f766e', '#134e4a', // Teal
    '#06b6d4', '#22d3ee', '#0e7490', '#164e63', // Cyan
    '#0ea5e9', '#38bdf8', '#0369a1', '#0c4a6e', // Sky
    '#3b82f6', '#60a5fa', '#1d4ed8', '#1e3a8a', // Blue
    '#6366f1', '#818cf8', '#4338ca', '#312e81', // Indigo
    '#8b5cf6', '#a78bfa', '#5b21b6', '#4c1d95', // Violet
    '#a855f7', '#c084fc', '#6b21a8', '#581c87', // Purple
    '#d946ef', '#e879f9', '#86198f', '#701a75', // Fuchsia
    '#ec4899', '#f472b6', '#9d174d', '#831843', // Pink
    '#f43f5e', '#fb7185', '#9f1239', '#881337', // Rose
    '#64748b', '#94a3b8', '#334155', '#0f172a', // Slate
    '#78716c', '#a8a29e', '#44403c', '#1c1917'  // Stone
];

export const UserSettingsModal: React.FC<Props> = ({ user, onClose, onUpdateProfile, onEinstellungenGespeichert, onOpenHolidayPlanner }) => {
    const [activeTab, setActiveTab] = useState<'profile' | 'vacation' | 'map' | 'design'>('profile');
    const design = useDesignMode();
    const [loading, setLoading] = useState(false);
    
    // Profile State
    const [firstName, setFirstName] = useState(user.first_name || '');
    const [lastName, setLastName] = useState(user.last_name || '');
    const [jobTitle, setJobTitle] = useState(user.job_title || '');
    const [phone, setPhone] = useState(user.phone || '');
    const [address, setAddress] = useState(user.address || '');
    
    // Password State
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [pwMessage, setPwMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // Vacation State
    const [vacations, setVacations] = useState<VacationRange[]>(user.vacation_plan || []);
    const [newVacation, setNewVacation] = useState<Partial<VacationRange>>({ startDate: '', endDate: '' });
    // Drei-Wege-Abgleich mit dem Server — ein Admin kann im Urlaubsplaner für
    // den Mitarbeiter eintragen, und das Profil aus App stammt vom Login:
    //  · baseVacationIds    — was zuletzt vom Server kam
    //  · deletedVacationIds — was der Nutzer in diesem Dialog gelöscht hat
    // Ein Server-Eintrag bleibt, solange der Nutzer ihn hier nicht gelöscht
    // hat; ein bekannter Eintrag, der auf dem Server fehlt, wurde anderswo
    // gelöscht und kommt nicht zurück.
    const baseVacationIds = useRef<Set<string>>(new Set((user.vacation_plan || []).map(v => v.id)));
    const deletedVacationIds = useRef<Set<string>>(new Set());
    // Zählt Speichervorgänge: ein Nachladen, das ein Speichern überholt, ist veraltet.
    const vacationSaves = useRef(0);

    const fetchServerVacations = async (): Promise<VacationRange[] | null> => {
        const { data, error } = await supabase.from('profiles').select('vacation_plan').eq('id', user.id).maybeSingle();
        if (error || !data) return null;
        return Array.isArray(data.vacation_plan) ? data.vacation_plan : [];
    };

    const mergeWithServer = (local: VacationRange[], server: VacationRange[], base: Set<string>): VacationRange[] => {
        const serverIds = new Set(server.map(v => v.id));
        const localIds = new Set(local.map(v => v.id));
        const kept = local.filter(v => !(base.has(v.id) && !serverIds.has(v.id)));
        const added = server.filter(v => !localIds.has(v.id) && !deletedVacationIds.current.has(v.id));
        return [...kept, ...added].sort((a, b) => a.startDate.localeCompare(b.startDate));
    };

    // Frisch nachladen, was seit dem Login dazukam oder anderswo gelöscht wurde.
    useEffect(() => {
        let cancelled = false;
        const savesAtStart = vacationSaves.current;
        fetchServerVacations().then(server => {
            if (cancelled || !server || vacationSaves.current !== savesAtStart) return;
            const base = baseVacationIds.current;
            setVacations(prev => mergeWithServer(prev, server, base));
            baseVacationIds.current = new Set(server.map(v => v.id));
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.id]);

    // Map Color State
    const [selectedColor, setSelectedColor] = useState<string>(user.settings?.mapColor || MAP_COLORS[0]);
    const [usedColors, setUsedColors] = useState<Set<string>>(new Set());

    // Auto-Save Refs
    const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isMounted = useRef(false);

    // Fetch used colors on mount
    useEffect(() => {
        const fetchUsedColors = async () => {
            // Find all colors used by OTHER users in the same company
            let query = supabase.from('profiles').select('id, settings');
            const filter = companyFilter(user);
            if (filter) {
                query = query.eq(filter.column, filter.value);
            }
            
            const { data } = await query;
            if (data) {
                const used = new Set<string>();
                data.forEach((p: any) => {
                    if (p.id !== user.id && p.settings?.mapColor) {
                        used.add(p.settings.mapColor);
                    }
                });
                setUsedColors(used);
            }
        };
        fetchUsedColors();
    }, [user]);

    // Live Auto-Save Effect (2s Delay)
    useEffect(() => {
        // Skip initial mount
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }

        // Only auto-save if we are on the profile tab
        if (activeTab !== 'profile') return;

        // Clear existing timer
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }

        // Set new timer
        autoSaveTimerRef.current = setTimeout(async () => {
            try {
                await onUpdateProfile({ 
                    first_name: firstName, 
                    last_name: lastName, 
                    job_title: jobTitle,
                    phone: phone, 
                    address: address 
                });
                // Optional: Console log for debug
                // console.log("Auto-saved profile data");
            } catch (e) {
                console.error("Auto-save failed", e);
            }
        }, 2000);

        return () => {
            if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        };
    }, [firstName, lastName, jobTitle, phone, address, activeTab]);

    const handleSaveProfile = async () => {
        // Clear pending auto-save to avoid double requests
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

        setLoading(true);
        try {
            await onUpdateProfile({ 
                first_name: firstName, 
                last_name: lastName, 
                job_title: jobTitle,
                phone: phone, 
                address: address 
            });
            // Handle Password Change if entered
            if (password) {
                if (password !== passwordConfirm) throw new Error("Passwörter stimmen nicht überein.");
                if (password.length < 6) throw new Error("Passwort muss mind. 6 Zeichen lang sein.");
                
                const { error } = await supabase.auth.updateUser({ password: password });
                if (error) throw error;
                
                setPwMessage({ type: 'success', text: "Profil und Passwort erfolgreich aktualisiert." });
                setPassword('');
                setPasswordConfirm('');
            }
            
            // Close modal on manual save
            onClose();

        } catch (e: any) {
            setPwMessage({ type: 'error', text: e.message || "Fehler beim Speichern." });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveMapColor = async () => {
        setLoading(true);
        try {
            // Nur den eigenen Schluessel mischen — nie das ganze Objekt aus dem
            // `user`-Prop (Stand vom Login) zurueckschreiben, sonst gingen
            // inzwischen anderswo gespeicherte Einstellungen still verloren.
            await patchProfileSettings(user.id, { mapColor: selectedColor });
            onEinstellungenGespeichert?.();
            setPwMessage({ type: 'success', text: "Kartenfarbe gespeichert." });
        } catch {
             setPwMessage({ type: 'error', text: "Fehler beim Speichern der Farbe." });
        } finally {
            setLoading(false);
        }
    };

    // Darstellung: wirkt sofort (Vorschau hinter dem Dialog) und wird gleich
    // gespeichert — kein eigener Speichern-Schritt. Gespeichert wie die
    // Kartenfarbe: nur der eigene Schluessel wird gemischt.
    // Nur ueberspringen, wenn die Wahl schon IM PROFIL steht — nicht nur im
    // Browser: nach einem gescheiterten Speichern (auch nach Neuladen) oder
    // einer Vorschau per Adresse speichert dieselbe Wahl erneut.
    const handleSaveDesign = async (mode: DesignMode) => {
        if (mode === design && designFromSettings(user.settings) === mode) return;
        setDesignMode(mode, user.id);
        setLoading(true);
        try {
            await patchProfileSettings(user.id, { design: mode });
            onEinstellungenGespeichert?.();
            setPwMessage({ type: 'success', text: mode === 'glas' ? 'Glas-Optik gespeichert.' : 'Klassische Optik gespeichert.' });
        } catch {
            setPwMessage({ type: 'error', text: 'Darstellung gilt nur vorläufig — Speichern im Profil fehlgeschlagen. Zum erneuten Versuch die Optik noch einmal wählen.' });
        } finally {
            setLoading(false);
        }
    };

    const addVacation = () => {
        if (!newVacation.startDate || !newVacation.endDate) return;
        if (newVacation.startDate > newVacation.endDate) return alert("Start muss vor Ende liegen.");

        const range: VacationRange = {
            id: crypto.randomUUID(),
            startDate: newVacation.startDate,
            endDate: newVacation.endDate
        };
        
        const updated = [...vacations, range].sort((a, b) => a.startDate.localeCompare(b.startDate));
        setVacations(updated);
        setNewVacation({ startDate: '', endDate: '' });
    };

    const removeVacation = (id: string) => {
        deletedVacationIds.current.add(id);
        setVacations(vacations.filter(v => v.id !== id));
    };

    const handleSaveVacation = async () => {
        setLoading(true);
        try {
            // Vor dem ersten await: ein noch laufendes Nachladen beim Öffnen
            // wird verworfen, Liste und Basis gelten als ein Stand.
            vacationSaves.current += 1;
            const local = vacations;
            const base = baseVacationIds.current;
            // Ohne aktuellen Serverstand nicht speichern: das ganze Array
            // würde sonst inzwischen anderswo eingetragene Urlaube löschen.
            const server = await fetchServerVacations();
            if (!server) {
                setPwMessage({ type: 'error', text: "Aktueller Urlaubsstand nicht ladbar. Bitte erneut speichern." });
                return;
            }
            const merged = mergeWithServer(local, server, base);
            await onUpdateProfile({ vacation_plan: merged });
            baseVacationIds.current = new Set(merged.map(v => v.id));
            setVacations(merged);
            setPwMessage({ type: 'success', text: "Urlaubsplan gespeichert." });
        } catch {
            setPwMessage({ type: 'error', text: "Fehler beim Speichern." });
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (d: string) => new Date(d).toLocaleDateString('de-DE');

    return (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col md:flex-row h-[80vh] md:h-[600px] animate-scale-in">
                
                {/* Sidebar */}
                <div className="bg-slate-900 text-white w-full md:w-64 p-6 shrink-0 flex flex-col">
                    <div className="mb-8">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-xl font-bold mb-3 shadow-lg" style={{ background: selectedColor ? selectedColor : undefined }}>
                            {(user.first_name?.[0] || 'U').toUpperCase()}
                        </div>
                        <h2 className="font-bold text-lg">{user.first_name} {user.last_name}</h2>
                        <p className="text-xs text-slate-400 truncate">{user.email}</p>
                        {user.job_title && <p className="text-xs text-blue-400 font-medium mt-1">{user.job_title}</p>}
                    </div>
                    
                    <nav className="space-y-2 flex-1">
                        <button 
                            onClick={() => setActiveTab('profile')}
                            className={`w-full flex items-center px-4 py-3 rounded-xl transition-all text-sm font-medium ${activeTab === 'profile' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                        >
                            <User className="w-4 h-4 mr-3" /> Profil & Sicherheit
                        </button>
                        <button 
                            onClick={() => setActiveTab('map')}
                            className={`w-full flex items-center px-4 py-3 rounded-xl transition-all text-sm font-medium ${activeTab === 'map' ? 'bg-orange-600 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                        >
                            <MapPin className="w-4 h-4 mr-3" /> Karten-Farbe
                        </button>
                        <button 
                            onClick={() => setActiveTab('vacation')}
                            className={`w-full flex items-center px-4 py-3 rounded-xl transition-all text-sm font-medium ${activeTab === 'vacation' ? 'bg-emerald-600 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                        >
                            <Palmtree className="w-4 h-4 mr-3" /> Meine Abwesenheit
                        </button>
                        <button
                            onClick={() => setActiveTab('design')}
                            className={`w-full flex items-center px-4 py-3 rounded-xl transition-all text-sm font-medium ${activeTab === 'design' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                        >
                            <Layers className="w-4 h-4 mr-3" /> Darstellung
                        </button>
                    </nav>

                    <button onClick={onClose} className="mt-auto flex items-center justify-center px-4 py-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors text-sm">
                        <X className="w-4 h-4 mr-2" /> Schließen
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 bg-slate-50 flex flex-col overflow-hidden">
                    <div className="p-6 md:p-8 flex-1 overflow-y-auto">
                        {activeTab === 'profile' && (
                            <div className="space-y-8 animate-slide-up">
                                <div>
                                    <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                                        <User className="w-5 h-5 mr-2 text-blue-600"/> Persönliche Daten
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                                        <div>
                                            <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Vorname</label>
                                            <input 
                                                type="text" 
                                                value={firstName} 
                                                onChange={(e) => setFirstName(e.target.value)}
                                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Nachname</label>
                                            <input 
                                                type="text" 
                                                value={lastName} 
                                                onChange={(e) => setLastName(e.target.value)}
                                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-xs font-bold text-slate-500 uppercase block mb-1 flex items-center gap-1">
                                                <Briefcase className="w-3 h-3"/> Position / Job-Titel
                                            </label>
                                            <input 
                                                type="text" 
                                                value={jobTitle} 
                                                onChange={(e) => setJobTitle(e.target.value)}
                                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                placeholder="z.B. SiGeKo, Geschäftsführer, Bauleiter"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-xs font-bold text-slate-500 uppercase block mb-1 flex items-center gap-1">
                                                <Phone className="w-3 h-3"/> Telefonnummer
                                            </label>
                                            <input 
                                                type="text" 
                                                value={phone} 
                                                onChange={(e) => setPhone(e.target.value)}
                                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                placeholder="+49 123 456789"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-xs font-bold text-slate-500 uppercase block mb-1 flex items-center gap-1">
                                                <Home className="w-3 h-3"/> Privatadresse / Home-Office
                                            </label>
                                            <input 
                                                type="text" 
                                                value={address} 
                                                onChange={(e) => setAddress(e.target.value)}
                                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                placeholder="Straße, PLZ, Ort (für Routenplanung)"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-xs font-bold text-slate-500 uppercase block mb-1">E-Mail (Login)</label>
                                            <input 
                                                type="text" 
                                                value={user.email} 
                                                disabled 
                                                className="w-full border border-slate-200 bg-slate-100 text-slate-500 rounded-lg px-3 py-2 text-sm cursor-not-allowed"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
                                        <Lock className="w-5 h-5 mr-2 text-blue-600"/> Passwort ändern
                                    </h3>
                                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
                                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-800">
                                            Lassen Sie die Felder leer, wenn Sie Ihr Passwort nicht ändern möchten.
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Neues Passwort</label>
                                                <input 
                                                    type="password" 
                                                    value={password} 
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                    placeholder="Mind. 6 Zeichen"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Bestätigen</label>
                                                <input 
                                                    type="password" 
                                                    value={passwordConfirm} 
                                                    onChange={(e) => setPasswordConfirm(e.target.value)}
                                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                    placeholder="Wiederholen"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'map' && (
                            <div className="space-y-6 animate-slide-up">
                                <div>
                                    <h3 className="text-xl font-bold text-slate-800 mb-1 flex items-center">
                                        <Palette className="w-5 h-5 mr-2 text-orange-600"/> Meine Karten-Farbe
                                    </h3>
                                    <p className="text-sm text-slate-500">
                                        Wählen Sie eine Farbe, mit der Ihre Projekte auf der Karte markiert werden.
                                        Bereits vergebene Farben sind ausgegraut.
                                    </p>
                                </div>

                                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                                    <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-3">
                                        {MAP_COLORS.map(color => {
                                            const isUsed = usedColors.has(color);
                                            const isSelected = selectedColor === color;
                                            
                                            return (
                                                <button
                                                    key={color}
                                                    onClick={() => !isUsed && setSelectedColor(color)}
                                                    disabled={isUsed}
                                                    className={`
                                                        w-8 h-8 rounded-full border-2 transition-all relative
                                                        ${isSelected ? 'border-slate-900 scale-125 shadow-lg' : 'border-transparent hover:scale-110'}
                                                        ${isUsed ? 'opacity-20 cursor-not-allowed border-slate-200' : ''}
                                                    `}
                                                    style={{ backgroundColor: color }}
                                                    title={isUsed ? 'Bereits vergeben' : 'Wählen'}
                                                >
                                                    {isSelected && <div className="absolute inset-0 flex items-center justify-center"><div className="w-2 h-2 bg-white rounded-full"></div></div>}
                                                    {isUsed && <div className="absolute inset-0 flex items-center justify-center"><X className="w-4 h-4 text-slate-900" /></div>}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800 flex items-start gap-2">
                                     <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                     <p>Ihre Auswahl hilft, Projekte auf der großen Übersichtskarte schneller zu identifizieren.</p>
                                </div>
                            </div>
                        )}

                        {activeTab === 'design' && (
                            <div className="space-y-6 animate-slide-up">
                                <div>
                                    <h3 className="text-xl font-bold text-slate-800 mb-1 flex items-center">
                                        <Layers className="w-5 h-5 mr-2 text-indigo-600"/> Darstellung
                                    </h3>
                                    <p className="text-sm text-slate-500">
                                        Wirkt sofort und gilt für Ihr Konto auf allen Geräten. Inhalte und Funktionen bleiben gleich.
                                    </p>
                                </div>

                                <div role="radiogroup" aria-label="Optik" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {([
                                        { mode: 'classic', title: 'Klassisch', text: 'Helle Flächen, Seitenleiste mit Beschriftung.' },
                                        { mode: 'glas', title: 'Glas', text: 'Durchscheinende Karten über einem Raum in GefAS-Blau, schmale Symbolleiste.' },
                                    ] as const).map(o => {
                                        const selected = design === o.mode;
                                        return (
                                            <button
                                                key={o.mode}
                                                type="button"
                                                role="radio"
                                                aria-checked={selected}
                                                disabled={loading}
                                                onClick={() => handleSaveDesign(o.mode)}
                                                className={`text-left bg-white rounded-xl border-2 p-3 transition-all disabled:opacity-60 ${selected ? 'border-indigo-600 shadow-md' : 'border-slate-200 hover:border-slate-300'}`}
                                            >
                                                {/* Kleine Vorschau: Leiste links, zwei Karten */}
                                                <div
                                                    aria-hidden="true"
                                                    className={`h-24 rounded-lg p-2 flex gap-2 ${o.mode === 'glas' ? 'bg-gradient-to-br from-[#4f76f0] via-[#c9d4ea] to-[#364fa2]' : 'bg-[#e9eef7]'}`}
                                                >
                                                    <div className={`w-5 rounded-md ${o.mode === 'glas' ? 'bg-white/45 border border-white/70' : 'bg-white border border-slate-200'}`} />
                                                    <div className="flex-1 flex flex-col gap-2">
                                                        <div className={`h-6 rounded-md ${o.mode === 'glas' ? 'bg-gradient-to-br from-[#3b8ff8] to-[#364fa2]' : 'bg-blue-600'}`} />
                                                        <div className={`flex-1 rounded-md ${o.mode === 'glas' ? 'bg-white/60 border border-white/80' : 'bg-white border border-slate-200'}`} />
                                                    </div>
                                                </div>
                                                <div className="mt-3 flex items-center justify-between gap-2">
                                                    <span className="text-sm font-bold text-slate-800">{o.title}</span>
                                                    {selected && <Check className="w-4 h-4 text-indigo-600 shrink-0" />}
                                                </div>
                                                <p className="text-xs text-slate-500 mt-0.5">{o.text}</p>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {activeTab === 'vacation' && (
                            <div className="space-y-6 animate-slide-up">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-800 mb-1 flex items-center">
                                            <Palmtree className="w-5 h-5 mr-2 text-emerald-600"/> Urlaubsplanung
                                        </h3>
                                        <p className="text-sm text-slate-500">
                                            Hinterlegen Sie hier Ihre Abwesenheiten. Die App erinnert Sie rechtzeitig daran, Vertreter für Ihre Projekte einzuteilen.
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-4">
                                        <div>
                                            <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Von</label>
                                            <input 
                                                type="date" 
                                                value={newVacation.startDate}
                                                onChange={(e) => setNewVacation({...newVacation, startDate: e.target.value})}
                                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Bis</label>
                                            <input 
                                                type="date" 
                                                value={newVacation.endDate}
                                                onChange={(e) => setNewVacation({...newVacation, endDate: e.target.value})}
                                                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                                            />
                                        </div>
                                        <Button onClick={addVacation} disabled={!newVacation.startDate || !newVacation.endDate} className="bg-emerald-600 hover:bg-emerald-700 w-full md:w-auto">
                                            <Plus className="w-4 h-4 mr-1"/> Hinzufügen
                                        </Button>
                                    </div>

                                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                                        {vacations.length === 0 ? (
                                            <div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-100 rounded-xl">
                                                Noch keine Urlaube geplant.
                                            </div>
                                        ) : (
                                            vacations.map(v => (
                                                <div key={v.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 group hover:border-emerald-300 transition-colors">
                                                    <div className="flex items-center gap-4">
                                                        <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600">
                                                            <Calendar className="w-4 h-4"/>
                                                        </div>
                                                        <div>
                                                            <div className="font-bold text-slate-700 text-sm">
                                                                {formatDate(v.startDate)} - {formatDate(v.endDate)}
                                                            </div>
                                                            <div className="text-xs text-slate-500">
                                                                {Math.ceil((new Date(v.endDate).getTime() - new Date(v.startDate).getTime()) / (1000 * 3600 * 24)) + 1} Tage
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {onOpenHolidayPlanner && (
                                                        <button 
                                                            onClick={() => onOpenHolidayPlanner(v.startDate, v.endDate)}
                                                            className="text-xs font-bold text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded transition-colors"
                                                        >
                                                            Vertreter planen
                                                        </button>
                                                        )}
                                                        <button 
                                                            onClick={() => removeVacation(v.id)}
                                                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                                        >
                                                            <Trash2 className="w-4 h-4"/>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer Actions */}
                    <div className="p-4 border-t border-slate-200 bg-white flex justify-between items-center">
                        <div>
                            {pwMessage && (
                                <span className={`text-sm flex items-center ${pwMessage.type === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {pwMessage.type === 'error' ? <AlertCircle className="w-4 h-4 mr-2"/> : <Save className="w-4 h-4 mr-2"/>}
                                    {pwMessage.text}
                                </span>
                            )}
                        </div>
                        {/* Darstellung speichert beim Wählen selbst. */}
                        {activeTab !== 'design' && (
                            <Button
                                variant="primary"
                                onClick={activeTab === 'profile' ? handleSaveProfile : (activeTab === 'map' ? handleSaveMapColor : handleSaveVacation)}
                                disabled={loading}
                                className={activeTab === 'profile' ? 'bg-blue-600' : (activeTab === 'map' ? 'bg-orange-600' : 'bg-emerald-600')}
                                icon={loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
                            >
                                Speichern
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
